import { UseQueryOptions, useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { add, format } from 'date-fns'
import { ILesson } from '../../../../../model/lesson.interface'
import { ITutor } from '../../../../../model/tutor.interface'
import apis from '../../../../apis'
import { getJwtToken } from '../../../../auth'
import { axiosPost } from '../../../../axiosHelper'
import QueryApi from '../../../../react-query/queryApiClass'
import {
  AvailableCoupon,
  AvailableCouponBundlesResponse,
  AvailableTutorsAtClassTimesResponse,
  IAvailableClassTimesResponse,
  IAvailableTutorsResponse,
  ISchedule,
} from './types'

interface IAvaialbleTutorQueryApiParams {
  duration: number
  classtime_id: number
}
const availableTutorQueryApi = new QueryApi<IAvaialbleTutorQueryApiParams>({
  api: apis.tutor.available,
})

export const useAvailableTutors = (duration: number, classtime_id: number) => {
  const token = getJwtToken()
  const pathParams = { duration, classtime_id }

  return useQuery<IAvailableTutorsResponse, AxiosError>({
    queryKey: availableTutorQueryApi.getQueryKey({ pathParams }),
    queryFn: availableTutorQueryApi.getQueryFn({ pathParams }),
    enabled: !!token,
  })
}

interface IAvaialbleClasstimesQueryApiSearchParams {
  duration: number
  with_favorite_tutor: boolean
  selected_coupon_ids?: number[]
}
const availableClasstimesQueryApi = new QueryApi<void, IAvaialbleClasstimesQueryApiSearchParams>({
  api: apis.classtimes.get,
})

export const useAvailableClasstimes = (
  duration: number,
  with_favorite_tutor: boolean,
  selected_coupon_ids: number[],
  options = {}
) => {
  const token = getJwtToken()
  const searchParams = { duration, with_favorite_tutor, selected_coupon_ids }
  return useQuery<IAvailableClassTimesResponse, AxiosError>({
    queryKey: availableClasstimesQueryApi.getQueryKey({ searchParams }),
    queryFn: availableClasstimesQueryApi.getQueryFn({ searchParams }),
    enabled: !!duration && !!token,
    ...options,
  })
}

export const useBookableSlots = (
  duration: number,
  with_favorite_tutor: boolean,
  selected_coupon_ids: number[],
  options = {}
) => {
  const token = getJwtToken()
  const searchParams = { duration, with_favorite_tutor, selected_coupon_ids }
  return useQuery<IAvailableClassTimesResponse[], AxiosError>({
    queryKey: availableClasstimesQueryApi.getQueryKey({ searchParams }),
    queryFn: availableClasstimesQueryApi.getQueryFn({ searchParams }),
    enabled: !!duration && !!token,
    ...options,
  })
}

export const useFindAvailableClasstime = (
  duration: number,
  with_favorite_tutor: boolean,
  selected_coupon_ids: number[],
  options = {}
) => {
  const token = getJwtToken()
  const searchParams = { duration, with_favorite_tutor, selected_coupon_ids }
  return useQuery<any, AxiosError>({
    queryKey: availableClasstimesQueryApi.getQueryKey({ searchParams }),
    queryFn: availableClasstimesQueryApi.getQueryFn({ searchParams }),
    enabled: !!duration && !!token,
    select: (data) => {
      if (data.length > 0) {
        const date = add(new Date(), { hours: 47 })
        let found = false
        let times = []
        const enabledDates = data?.filter(
          (classtime) => classtime?.classtimes?.filter((time) => time.is_opened)?.length > 0
        )
        if (enabledDates.length > 0) {
          while (!found) {
            const selectedDate = format(date, 'yyyy-MM-dd')
            times = data.find((item) => item.date == selectedDate)?.classtimes
            const availableTimes = times?.filter((item) => item.is_opened)
            if (availableTimes && availableTimes.length >= 1) {
              found = true
            } else {
              date.setDate(date.getDate() + 1)
            }
          }
        }
        const classtimeId = enabledDates.find((item) => item.date == format(date, 'yyyy-MM-dd'))?.classtimes[0].id
        return classtimeId
        // setClasstimeId(classtimeId)
      }
    },
    ...options,
  })
}

interface IAvaialbleCouponBundlesQueryApiPathParams {
  classtime_id: number
}

interface IAvaialbleCouponBundlesQueryApiSearchParams {
  duration: number
}
const availableCouponBundlesQueryApi = new QueryApi<
  IAvaialbleCouponBundlesQueryApiPathParams,
  IAvaialbleCouponBundlesQueryApiSearchParams
>({
  api: apis.classtimes.couponBundles,
})

export const useAvailableCouponBundles = (classtime_id: number, duration: number, options = {}) => {
  const token = getJwtToken()
  const pathParams = { classtime_id }
  const searchParams = { duration }
  return useQuery<AvailableCouponBundlesResponse, AxiosError>({
    queryKey: availableCouponBundlesQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: availableCouponBundlesQueryApi.getQueryFn({ pathParams, searchParams }),
    enabled: !!classtime_id && !!token && !!duration,
    retry: false,
    ...options,
  })
}

interface IAvaialbleCouponQueryApiPathParams {
  classtime_id: number
}

interface IAvaialbleCouponQueryApiSearchParams {
  coupon_bundle_id?: number
  coupon_type_id?: number
  selected_coupon_ids?: number[]
}
const availableCouponQueryApi = new QueryApi<IAvaialbleCouponQueryApiPathParams, IAvaialbleCouponQueryApiSearchParams>({
  api: apis.classtimes.availableCoupon,
})

export const useAvailableCoupon: any = (applyInfo, selected_coupon_ids: number[], options = {}) => {
  const token = getJwtToken()
  const { selectedCoupon, selectedTime, selectedTimeslot } = applyInfo
  const pathParams = selectedTime
    ? { classtime_id: selectedTime?.classtime?.id }
    : { classtime_id: selectedTimeslot?.classtime_id }
  const searchParams = {
    coupon_type_id: selectedCoupon?.couponTypeId,
    coupon_bundle_id: selectedCoupon?.couponBundleId,
    selected_coupon_ids: [],
  }
  return useQuery<AvailableCoupon, AxiosError>({
    queryKey: availableCouponQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: availableCouponQueryApi.getQueryFn({ pathParams, searchParams }),
    enabled: (!!pathParams || !!selectedTimeslot) && !!token && !!selectedCoupon,
    ...options,
  })
}

interface IAvaialbleTutorsAtClasstimesQueryApiParams {
  classtime_id: number
}
interface IAvaialbleTutorsAtClasstimesQueryApiSearchParams {
  duration: number
}
const availableTutorsAtClasstimesQueryApi = new QueryApi<
  IAvaialbleTutorsAtClasstimesQueryApiParams,
  IAvaialbleTutorsAtClasstimesQueryApiSearchParams
>({
  api: apis.classtimes.tutors,
})

export const useAvailableTutorsAtClasstimes = (classtime_id: number, duration: number, options = {}) => {
  const token = getJwtToken()

  const searchParams = { duration }

  const classtimeIds = [classtime_id - 1, classtime_id, classtime_id + 1]

  return useQueries({
    queries: classtimeIds.map<UseQueryOptions<AvailableTutorsAtClassTimesResponse, AxiosError>>((classtimeId) => {
      const pathParams = { classtime_id: classtimeId }
      return {
        queryKey: availableTutorsAtClasstimesQueryApi.getQueryKey({ pathParams, searchParams }),
        queryFn: () =>
          availableTutorsAtClasstimesQueryApi
            .getQueryFn({ pathParams, searchParams })()
            .then((response: ITutor[]) => ({
              tutors: response,
              classtimeId: classtimeId,
            })),
        enabled: !!classtime_id && !!duration && !!token,
        ...options,
      }
    }),
  })
}

export const useAvailableTutorsAtClasstime = (classtime_id: number, duration: number, options = {}) => {
  const token = getJwtToken()

  const searchParams = { duration }
  const pathParams = { classtime_id: classtime_id }

  return useQuery<AvailableTutorsAtClassTimesResponse, AxiosError>({
    queryKey: availableTutorsAtClasstimesQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: () =>
      availableTutorsAtClasstimesQueryApi
        .getQueryFn({ pathParams, searchParams })()
        .then((response: ITutor[]) => ({
          tutors: response,
          classtimeId: classtime_id,
        })),
    enabled: !!classtime_id && !!duration && !!token,
    ...options,
  })
}
interface IStartTimeLimitSearchParams {
  duration: number
  selected_coupon_ids?: number[]
}
interface IStartTimeLimitResponse {
  message: string
}
const startTimeLimitQueryApi = new QueryApi<void, IStartTimeLimitSearchParams>({
  api: apis.lessons.startTimeLimit,
})

export const useStartTimeLimit = (duration: number, selected_coupon_ids: number[], options = {}) => {
  const token = getJwtToken()
  const searchParams = { duration, selected_coupon_ids }
  return useQuery<IStartTimeLimitResponse, AxiosError>({
    queryKey: startTimeLimitQueryApi.getQueryKey({ searchParams }),
    queryFn: startTimeLimitQueryApi.getQueryFn({ searchParams }),
    enabled: !!duration && !!token,
    ...options,
  })
}

interface IAvaialbleTimesForTutorQueryApiPathParams {
  tutorId: number
}
interface IAvaialbleTimesForTutorQueryApiSearchParams {
  duration: number
}
const availableTimesForTutorQueryApi = new QueryApi<
  IAvaialbleTimesForTutorQueryApiPathParams,
  IAvaialbleTimesForTutorQueryApiSearchParams
>({
  api: apis.tutor.timeslots,
})

export const useAvailableTimesForTutor = (tutorId: number, duration: number, options = {}) => {
  const token = getJwtToken()
  const pathParams = { tutorId }
  const searchParams = { duration }

  return useQuery<AvailableCouponBundlesResponse, AxiosError>({
    queryKey: availableTimesForTutorQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: availableTimesForTutorQueryApi.getQueryFn({ pathParams, searchParams }),
    enabled: !!tutorId && !!duration && !!token,
    ...options,
  })
}

interface IHoldTimeslotResponse {
  success: boolean
  error: string
}
interface IHoldTimeslotPayload {
  duration: number
}
export const useHoldTimeslot = (timeslotId: number) => {
  return useMutation<IHoldTimeslotResponse, AxiosError, IHoldTimeslotPayload>({
    mutationFn: ({ duration }) => {
      return axiosPost(apis.timeslots.hold({ timeslotId }), {
        duration: duration,
      })
    },
    // onError: (error) => handleAxiosError(error),
  })
}

interface IScheduleRersultLesson {
  success: boolean
  is_rookie_lesson: boolean
  is_rookie_tutor: boolean
  message: string
  lesson: ILesson
  tutor: ITutor
}
interface IApplyLessonsResponse {
  is_first: boolean
  results: IScheduleRersultLesson[]
}
export interface IScheduleResult {
  data: IApplyLessonsResponse
  schedules: ISchedule[]
}

interface IApplyLesson {
  duration: number
  matching_after?: number
  timeslot_id?: number
  classtime_id?: number
  coupon_bundle_id?: number
  coupon_type_id?: number
}
interface IApplyLessonsPayload {
  application_objects: IApplyLesson[]
}

export const useApplyLessons = () => {
  return useMutation<IApplyLessonsResponse, AxiosError, IApplyLessonsPayload>({
    mutationFn: (payload) => {
      return axiosPost(apis.timeslots.apply(), payload)
    },
  })
}
